import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import App from "./App";
import { ApolloClient, ApolloLink, ApolloProvider, createHttpLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";

const URL_LOCAL = "http://localhost:5001/";
const URL_DEV = "https://dev.gdzietometro.pl/apollo";
const URL_PROD = "https://prod.gdzietometro.pl/apollo";

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        padding: '20px',
        height: '100vh', // Center vertically
    },
    label: {
        fontSize: '18px',
        marginBottom: '10px',
    },
    input: {
        height: '40px',
        borderColor: 'gray',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: '5px',
        padding: '10px',
        width: '50%', // Restrict to 50% of screen width
        marginBottom: '20px',
    },
    button: {
        padding: '10px 20px',
        backgroundColor: '#007BFF',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        marginBottom: '20px',
    },
    switchContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px', // Add space between checkboxes
        marginBottom: '20px',
    },
    switchLabel: {
        fontSize: '16px',
        marginLeft: '8px', // Add space between checkbox and label
    },
};

function Root() {
    const [pass, setPass] = useState(null);
    const [text, setText] = useState('');
    const [url, setUrl] = useState(URL_DEV); // State to manage selected URL

    // Utility function to get a cookie by name
    const getCookie = (name) => {
        const matches = document.cookie.match(new RegExp(
            `(?:^|; )${name.replace(/([\.$?*|{}()\[\]\\\/\+^])/g, '\\$1')}=([^;]*)`
        ));
        return matches ? decodeURIComponent(matches[1]) : undefined;
    };

    // Utility function to set a cookie
    const setCookie = (name, value, options = {}) => {
        options = {
            path: '/',
            ...options,
        };

        if (options.expires instanceof Date) {
            options.expires = options.expires.toUTCString();
        }

        let updatedCookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

        for (let optionKey in options) {
            updatedCookie += `; ${optionKey}`;
            const optionValue = options[optionKey];
            if (optionValue !== true) {
                updatedCookie += `=${optionValue}`;
            }
        }

        document.cookie = updatedCookie;
    };

    // Check for saved pass in cookies on component mount
    useEffect(() => {
        const savedPass = getCookie('auth_pass');
        const savedServer = getCookie('auth_server');
        if (savedPass) {
            setPass(savedPass);
        }
        if (savedServer) {
            setUrl(savedServer === 'prod' ? URL_PROD : URL_DEV);
        }
    }, []);

    const authMiddleware = setContext((_, { headers }) => ({
        headers: {
            ...headers,
            Authorization: pass,
        },
    }));

    const httpLink = createHttpLink({ uri: url });

    const client = useMemo(() => {
        return new ApolloClient({
            link: ApolloLink.from([authMiddleware, httpLink]),
            cache: new InMemoryCache({
                addTypename: false,
            }),
        });
    }, [pass, url]);

    const handleUrlSwitch = (newUrl) => {
        setUrl(newUrl);
    };

    if (pass === null)
        return (
            <div style={styles.container}>
                <p style={styles.label}>Wprowadź hasło admina:</p>
                <input
                    type="password" // Makes the input type password (hides characters)
                    style={styles.input}
                    placeholder="Wpisz hasło autoryzacji..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            setPass(text);
                            setCookie('auth_pass', text, { expires: new Date(Date.now() + 3600 * 1000) });
                            setCookie('auth_server', url === URL_PROD ? 'prod' : 'dev', { expires: new Date(Date.now() + 3600 * 1000) });
                        }
                    }}
                />
                <div style={styles.switchContainer}>
                    <div>
                        <input
                            type="checkbox"
                            id="prodUrl"
                            checked={url === URL_PROD}
                            onChange={() => handleUrlSwitch(URL_PROD)}
                            style={{ cursor: 'pointer' }}
                        />
                        <label htmlFor="prodUrl" style={styles.switchLabel}>
                            Używaj Prod
                        </label>
                    </div>
                    <div>
                        <input
                            type="checkbox"
                            id="devUrl"
                            checked={url === URL_DEV}
                            onChange={() => handleUrlSwitch(URL_DEV)}
                            style={{ cursor: 'pointer' }}
                        />
                        <label htmlFor="devUrl" style={styles.switchLabel}>
                            Używaj Dev
                        </label>
                    </div>
                </div>
                <button
                    style={styles.button}
                    onClick={() => {
                        setPass(text); // Set the pass state
                        setCookie('auth_pass', text, { expires: new Date(Date.now() + 3600 * 1000) });
                        setCookie('auth_server', url === URL_PROD ? 'prod' : 'dev', { expires: new Date(Date.now() + 3600 * 1000) });
                    }}
                >
                    Wyślij
                </button>
            </div>
        );
    else
        return (
            <ApolloProvider client={client}>
                <App />
            </ApolloProvider>
        );
}

ReactDOM.render(<Root />, document.getElementById('root'));
